import React, { useEffect } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useContentToken,
  usePropertyField,
  useSlotGroup,
  useCollectionBinding,
  usePrimaryColor,
  useTokens,
} from '@vibesites/runtime/react';
import { EditableToken, EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import {
  MultihubLogoMonoWhite,
  MultihubLogoMonoBlack,
  repliWhiteLogoUrl,
  FairHousingIcon,
  PetsAllowedIcon,
  WheelchairIcon,
  FacebookIcon,
  InstagramIcon,
  TwitterIcon,
  YouTubeIcon,
  TikTokIcon,
  LinkedInIcon,
} from '@vibesites/assets';
import { VibesitesImage } from '@vibesites/ui-primitives';
// Local black variant of the Repli wordmark — `@vibesites/assets` only ships
// `repliWhiteLogoUrl`, so we keep a dark-fill copy alongside it for use on
// light primary backgrounds. Source SVG mirrors `repli_white.svg` with the
// wordmark's `fill:#fff` swapped to `#1a1a1a` (matches the dark color used
// by the tokens generator's `getContrastTextColor`).
import repliBlackLogoUrl from '../assets/repli_black.svg';

// ============================================================================
// Why this fork still exists
// ----------------------------------------------------------------------------
// This Footer was previously a full divergent copy of the rise theme's Footer.
// It has been re-synced to mirror `@vibesites/theme-rise`'s Footer verbatim so
// that any edits made upstream (phone styling, "Professionally Managed by"
// label, attribution row alignment, etc.) are reflected here — the only
// remaining Weidner-specific addition is the "Text Us!" link rendered between
// the Phone and Address blocks in the contact column. The parent Footer
// doesn't expose a slot at that position, so the link cannot be injected via
// pages.json and the entire footer must be re-rendered to host it.
//
// When updating this file:
//   1. Diff against `@vibesites/theme-rise/components/Footer.tsx`.
//   2. The only intentional differences are:
//        - `textUsLinkDef` slot group definition
//        - `useSlotGroup('footer.text_us_link', …)` call
//        - `showTextUsLink` suppression check (hides the row when no
//          `custom_text_us_url` is set on the property)
//        - The `{showTextUsLink && (…)}` block rendered between Phone and
//          Address in the contact column
//        - The `'footer.text_us_link': textUsLinkDef` entry in `slotGroups`
//        - Weidner Cares + Love Notes program-logo slot groups (and their
//          `useSlotGroup` calls, `resolveProgramLogo` renderer, render
//          block beneath the community logo, and schema entries)
//        - Social link icon row beneath the office hours column —
//          `{facebook,instagram,twitter,youtube,tiktok,linkedin}SocialDef`
//          slot defs, their `useSlotGroup` calls, the `socials` /
//          `visibleSocials` arrays, the icon row inside Column 3, and the
//          matching `'footer.<platform>_social'` schema entries
//        - CORT lead-form CTA beneath office hours (shown only when
//          `custom_cort_lead_form_id` is set): `footer.cort_button_text`
//          token, `usePropertyField('custom_cort_lead_form_id')`,
//          CortFormLoader script injection (`loadCortForm(id, 'Custom')`),
//          `openCortModal` → `window.Cort_modal.open()`, JSX + `.footer-cort-btn*` styles
//      Everything else should match the parent — INCLUDING the RentCafe IQ
//      chatbot z-index pin (the `useEffect` MutationObserver block above
//      the `return` and the `<style>` block immediately above the closing
//      `</footer>`). The parent uses Footer as a global mount point for
//      that fix since Footer renders on every page via DefaultLayout; if
//      this fork drops it, the chatbot widget falls behind floating page
//      elements (sticky header, hero CTAs, modals) on Weidner properties.
//      Sync any future changes to those two blocks from the parent verbatim.
// ============================================================================

// ============================================================================
// Contrast text color
// ----------------------------------------------------------------------------
// Mirrors `getContrastTextColor` from `@vibesites/tokens` (defined in
// `vendor/vibesites/tokens/generator.ts`, currently module-private and not
// re-exported, so we replicate the exact logic here verbatim — including the
// `0.55` luminance threshold and the `'#ffffff'` / `'#1a1a1a'` return values).
// Using the same function keeps logo color choice (white vs black) in lockstep
// with the auto-computed `--vibe-color-text-on-primary` /
// `--vibe-color-text-on-secondary` CSS variables, so the footer's text and
// logos always pick the same contrast side. Elements rendered against the
// primary bg (community logo + program-icon pair) pivot on the primary
// luminance; the lower credit band runs on the secondary bg, so its
// Managed By / Multihub / Repli marks pivot on the secondary luminance.
// ============================================================================

const TEXT_ON_PRIMARY_LIGHT = '#ffffff';
const TEXT_ON_PRIMARY_DARK = '#1a1a1a';

// CORT furnished-apartments lead form — mirrors `@vibesites/blocks` cort-lead-form
// so the footer CTA can open `window.Cort_modal` without requiring a separate
// injection-block mount in the editor.
const CORT_SCRIPT_ID = 'CortFormLoader';
const CORT_FORM_DIV_ID = 'CortFormDiv';
const CORT_SCRIPT_URL = 'https://api-assets.cort.com/scripts/CortFormLoader.js';
// Stylesheets referenced by the form HTML. `<link>` tags injected via
// `innerHTML` into `#CortFormDiv` are unreliable across browsers, so we mount
// them ourselves before calling `loadCortForm`.
const CORT_STYLE_URLS = [
  'https://api-assets.cort.com/styles/tingle.min.css',
  'https://api-assets.cort.com/styles/cort_leadform_v2.css',
] as const;

type CortModalApi = { open?: () => void };
type CortWindow = Window & {
  Cort_modal?: CortModalApi;
  loadCortForm?: (id: string, mode?: string) => void | Promise<void>;
};

function getCortWindow(): CortWindow | undefined {
  if (typeof window === 'undefined') return undefined;
  return window as CortWindow;
}

function getCortModal(): CortModalApi | undefined {
  return getCortWindow()?.Cort_modal;
}

function ensureCortStylesheets() {
  if (typeof document === 'undefined') return;
  for (const href of CORT_STYLE_URLS) {
    if (document.querySelector(`link[data-cort-style="${href}"]`)) continue;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.crossOrigin = 'anonymous';
    link.setAttribute('data-cort-style', href);
    document.head.appendChild(link);
  }
}

function ensureCortFormHost() {
  if (typeof document === 'undefined') return;
  let formDiv = document.getElementById(CORT_FORM_DIV_ID);
  if (!formDiv) {
    formDiv = document.createElement('div');
    formDiv.id = CORT_FORM_DIV_ID;
    document.body.appendChild(formDiv);
  }
  // Keep the host in-flow for stylesheet/script side effects, but visually
  // collapsed. `hidden` can prevent some browsers from applying nested
  // stylesheets that CORT injects into this node.
  formDiv.setAttribute('aria-hidden', 'true');
  formDiv.style.cssText =
    'position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0;';
}

function loadCortScript(): Promise<void> {
  const win = getCortWindow();
  if (!win || typeof document === 'undefined') return Promise.resolve();
  if (typeof win.loadCortForm === 'function') return Promise.resolve();

  const existing = document.getElementById(CORT_SCRIPT_ID) as HTMLScriptElement | null;
  if (existing) {
    return new Promise((resolve, reject) => {
      if (typeof getCortWindow()?.loadCortForm === 'function') {
        resolve();
        return;
      }
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('CORT script failed to load')), { once: true });
    });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.id = CORT_SCRIPT_ID;
    script.src = CORT_SCRIPT_URL;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('CORT script failed to load'));
    document.body.appendChild(script);
  });
}

function waitForCortModal(timeoutMs = 15000): Promise<CortModalApi | undefined> {
  const existing = getCortModal();
  if (existing?.open) return Promise.resolve(existing);

  return new Promise((resolve) => {
    const started = Date.now();
    const timer = window.setInterval(() => {
      const modal = getCortModal();
      if (modal?.open) {
        window.clearInterval(timer);
        resolve(modal);
        return;
      }
      if (Date.now() - started >= timeoutMs) {
        window.clearInterval(timer);
        resolve(undefined);
      }
    }, 100);
  });
}

async function ensureCortFormReady(formId: string): Promise<CortModalApi | undefined> {
  const ready = getCortModal();
  if (ready?.open) return ready;

  ensureCortStylesheets();
  ensureCortFormHost();
  await loadCortScript();

  const win = getCortWindow();
  if (typeof win?.loadCortForm === 'function') {
    // Re-init when modal isn't ready yet (first load, or prior init still pending).
    await Promise.resolve(win.loadCortForm(formId, 'Custom'));
  }

  return waitForCortModal();
}

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const cleaned = hex.replace('#', '').trim();
  if (cleaned.length === 3) {
    return {
      r: parseInt(cleaned[0] + cleaned[0], 16),
      g: parseInt(cleaned[1] + cleaned[1], 16),
      b: parseInt(cleaned[2] + cleaned[2], 16),
    };
  }
  if (cleaned.length === 6) {
    return {
      r: parseInt(cleaned.slice(0, 2), 16),
      g: parseInt(cleaned.slice(2, 4), 16),
      b: parseInt(cleaned.slice(4, 6), 16),
    };
  }
  return null;
}

function getRelativeLuminance(hex: string): number {
  const rgb = hexToRgb(hex);
  if (!rgb) return 0;
  const [rs, gs, bs] = [rgb.r / 255, rgb.g / 255, rgb.b / 255].map((c) =>
    c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  );
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

/**
 * Returns the contrasting text color (`'#ffffff'` or `'#1a1a1a'`) for a given
 * background hex. Identical to `getContrastTextColor` in `@vibesites/tokens`.
 * Falls back to the white-on-dark side when the input can't be parsed, which
 * matches how the tokens generator treats unknown / missing primary colors.
 */
function getContrastTextColor(bgHex: string | null | undefined): string {
  if (!bgHex) return TEXT_ON_PRIMARY_LIGHT;
  const luminance = getRelativeLuminance(bgHex);
  // Favor white text on medium tones (gold, tan, dusty blue) where dark text is
  // technically higher-contrast but reads as heavy/muddy. Flip to dark only on
  // genuinely light backgrounds (pastels, cream, near-white).
  return luminance > 0.55 ? TEXT_ON_PRIMARY_DARK : TEXT_ON_PRIMARY_LIGHT;
}

/**
 * Returns true if `hex` is closer to white than to black. Used to translate
 * the runtime's already-computed `textOnSecondary` / `textOnPrimary` token
 * into the binary white/black decision the SVG-variant pickers need, so the
 * lower band's icons, labels, and inline mono logos all move in lockstep
 * with the `--vibe-color-text-on-*` CSS variables. Mid-line luminance check
 * (0.5) is intentionally generous — the runtime only ever emits `#ffffff`
 * or `#1a1a1a` for the auto-computed contrast tokens, but a theme or
 * property could override with any hex and we still want a sensible split.
 */
function isWhiteish(hex: string | null | undefined): boolean {
  if (!hex) return true;
  return getRelativeLuminance(hex) > 0.5;
}

// ============================================================================
// Types
// ============================================================================

interface OfficeHour {
  day?: string | null;
  open_time?: string | null;
  close_time?: string | null;
}

function hasValue(val: string | null | undefined): val is string {
  if (!val) return false;
  const normalized = val.trim().toLowerCase();
  return normalized !== '' && normalized !== 'false' && normalized !== 'null' && normalized !== 'undefined';
}

/** Formats a phone string into (XXX) XXX-XXXX. Passes through if already formatted or unrecognizable. */
function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  // Strip leading country code "1" if 11 digits
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length !== 10) return raw;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

function formatTime(raw: string): string {
  const trimmed = raw.trim();
  // Already has am/pm — just normalize casing
  if (/[ap]m/i.test(trimmed)) {
    return trimmed.replace(/([ap]m)/i, (m) => m.toLowerCase());
  }
  // Parse 24h "HH:MM" or "H:MM"
  const match = trimmed.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return trimmed;

  let hours = parseInt(match[1], 10);
  const minutes = match[2];
  const period = hours >= 12 ? 'pm' : 'am';
  if (hours === 0) hours = 12;
  else if (hours > 12) hours -= 12;
  return `${hours}:${minutes} ${period}`;
}

const DAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_LABELS: Record<string, string> = {
  mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday', thu: 'Thursday',
  fri: 'Friday', sat: 'Saturday', sun: 'Sunday',
};
const DAY_SHORT_LABELS: Record<string, string> = {
  mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu',
  fri: 'Fri', sat: 'Sat', sun: 'Sun',
};

interface GroupedHours {
  label: string;
  shortLabel: string;
  hours: string;
}

function normalizeDay(day: string): string {
  return day.trim().toLowerCase().slice(0, 3);
}

function groupOfficeHours(raw: OfficeHour[]): GroupedHours[] {
  // Index the raw entries by canonical day key (mon/tue/...). Anything that
  // doesn't match a known day gets dropped — there's no sensible place to
  // render a "Funday" row, and the editor won't surface non-canonical values
  // either. Within a known day, the first entry wins (matches the previous
  // implicit behavior of iterating the collection in order).
  const byDay: Record<string, OfficeHour> = {};
  for (const entry of raw) {
    if (!hasValue(entry.day)) continue;
    const key = normalizeDay(entry.day);
    if (DAY_ORDER.indexOf(key) === -1) continue;
    if (!(key in byDay)) byDay[key] = entry;
  }

  const groups: GroupedHours[] = [];

  // Walk all seven days in canonical order so missing days render as "Closed"
  // instead of being silently dropped. The append-or-extend logic below then
  // merges consecutive same-hours days (including consecutive closed days),
  // so a Mon–Fri 9–5 schedule with no Sat/Sun entries renders as
  // "Monday - Friday: 9:00 am - 5:00 pm" + "Saturday - Sunday: Closed".
  for (const key of DAY_ORDER) {
    const entry = byDay[key];
    const dayLabel = DAY_LABELS[key];
    const dayShortLabel = DAY_SHORT_LABELS[key];

    // Missing day OR present-but-blank open/close both render as Closed.
    const isClosed =
      !entry || (!hasValue(entry.open_time) && !hasValue(entry.close_time));
    const hoursStr = isClosed
      ? 'Closed'
      : `${formatTime(entry!.open_time || '')} - ${formatTime(entry!.close_time || '')}`;

    const prev = groups[groups.length - 1];
    if (prev && prev.hours === hoursStr) {
      // Extend the previous group's range — keep both the full and short label
      // ranges in sync so the responsive renderer can swap them at the tablet
      // breakpoint without re-deriving the grouping.
      const dash = prev.label.indexOf(' - ');
      const startLabel = dash !== -1 ? prev.label.slice(0, dash) : prev.label;
      prev.label = `${startLabel} - ${dayLabel}`;
      const shortDash = prev.shortLabel.indexOf(' - ');
      const startShort = shortDash !== -1 ? prev.shortLabel.slice(0, shortDash) : prev.shortLabel;
      prev.shortLabel = `${startShort} - ${dayShortLabel}`;
    } else {
      groups.push({ label: dayLabel, shortLabel: dayShortLabel, hours: hoursStr });
    }
  }

  return groups;
}

// ============================================================================
// Slot Group Definitions
// ============================================================================

const logoSlotDef: SlotGroupDefinition = {
  label: 'Footer Logo',
  fields: {
    image: { label: 'Logo Image', type: 'image', defaultField: 'property_mono_white_logo', suggestedFields: ['property_mono_white_logo', 'property_primary_logo', 'property_secondary_logo'] },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
  },
};

const managedByLogoDef: SlotGroupDefinition = {
  label: 'Managed By Logo',
  fields: {
    image: { label: 'Logo Image', type: 'image', defaultField: 'property_company_mono_white_logo', suggestedFields: ['property_company_mono_white_logo', 'property_company_logo', 'property_company_mono_black_logo'] },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Property Management Company Logo' },
    url: { label: 'URL', type: 'url', defaultField: 'property_company_url' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const poweredByLogoDef: SlotGroupDefinition = {
  label: 'Powered By Logo',
  fields: {
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Multihub' },
    url: { label: 'URL', type: 'url', defaultValue: 'https://www.multihub.io/' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const designByLogoDef: SlotGroupDefinition = {
  label: 'Website Design By Logo',
  fields: {
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Repli Logo' },
    url: { label: 'URL', type: 'url', defaultValue: 'https://www.repli360.com/' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const addressLinkDef: SlotGroupDefinition = {
  label: 'Address',
  fields: {
    text: { label: 'Address Text', type: 'text', defaultField: 'property_full_address', defaultValue: '123 Main Street, City, ST 00000' },
    url: { label: 'URL', type: 'url', defaultField: 'property_google_place_url' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

// Weidner-specific: "Text Us!" link. URL binds to the per-property
// `custom_text_us_url` custom field — the link is suppressed entirely if no
// URL is provided so properties without an SMS landing page don't render a
// dead link.
const textUsLinkDef: SlotGroupDefinition = {
  label: 'Text Us Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Text Us!' },
    url: { label: 'URL', type: 'url', defaultField: 'custom_text_us_url' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const accessibilityLinkDef: SlotGroupDefinition = {
  label: 'Accessibility Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Accessibility' },
    url: { label: 'URL', type: 'url', defaultValue: '/accessibility' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const privacyPolicyLinkDef: SlotGroupDefinition = {
  label: 'Privacy Policy Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Privacy Policy' },
    url: { label: 'URL', type: 'url', defaultValue: '/privacy-policy' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const sitemapLinkDef: SlotGroupDefinition = {
  label: 'Sitemap Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Sitemap' },
    url: { label: 'URL', type: 'url', defaultValue: '/sitemap' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// Weidner-specific program logos. Editors upload a PNG/SVG via the `image`
// field (the white / light-on-dark variant) and, optionally, a dark variant
// via the `image_dark` field for use on light primary brand colors. The
// renderer picks between the two using the same `useWhiteOnPrimary` luminance
// pivot that swaps the community / managed-by / Multihub / Repli logos, so
// these program logos always sit on the correct contrast side. If only one
// variant has been uploaded, the renderer falls back to whichever is set so
// nothing visually disappears on properties with a single asset.
const weidnerCaresLogoDef: SlotGroupDefinition = {
  label: 'Weidner Cares Logo',
  fields: {
    image: { label: 'Logo Image (Light)', type: 'image', defaultValue: '' },
    image_dark: { label: 'Logo Image (Dark)', type: 'image', defaultValue: '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Weidner Cares' },
    url: { label: 'URL', type: 'url', defaultValue: 'https://www.weidner.com/weidnercares' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const loveNotesLogoDef: SlotGroupDefinition = {
  label: 'Love Notes Logo',
  fields: {
    image: { label: 'Logo Image (Light)', type: 'image', defaultValue: '' },
    image_dark: { label: 'Logo Image (Dark)', type: 'image', defaultValue: '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Love Notes' },
    url: { label: 'URL', type: 'url', defaultValue: 'https://www.weidner.com/lovenotes' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

// ----------------------------------------------------------------------------
// Social link slot groups
// ----------------------------------------------------------------------------
// Rendered as an icon row directly beneath the office hours column. Each slot
// binds its URL to a canonical `property_<platform>_url` field by default so
// properties get pre-populated icons without any editor action — and any icon
// whose URL is empty is suppressed at render time (see the `socials` array
// inside the component) so the row only shows platforms that are actually
// configured. Icons themselves come from `@vibesites/assets` (Font Awesome
// brand glyphs under the hood) and render at `currentColor`, so they inherit
// the `text-primary-foreground` color of the footer body and stay
// contrast-correct on both dark and light primary brand colors.
// ----------------------------------------------------------------------------

const facebookSocialDef: SlotGroupDefinition = {
  label: 'Facebook',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_facebook_url',
      suggestedFields: ['property_facebook_url'],
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const instagramSocialDef: SlotGroupDefinition = {
  label: 'Instagram',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_instagram_url',
      suggestedFields: ['property_instagram_url'],
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const twitterSocialDef: SlotGroupDefinition = {
  label: 'Twitter / X',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_twitter_url',
      suggestedFields: ['property_twitter_url'],
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const youtubeSocialDef: SlotGroupDefinition = {
  label: 'YouTube',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: 'https://www.youtube.com/@WeidnerApartmentHomes',
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const tiktokSocialDef: SlotGroupDefinition = {
  label: 'TikTok',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_tiktok_url',
      suggestedFields: ['property_tiktok_url'],
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const linkedinSocialDef: SlotGroupDefinition = {
  label: 'LinkedIn',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_linkedin_url',
      suggestedFields: ['property_linkedin_url'],
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

// ============================================================================
// Component
// ============================================================================

export function Footer() {
  const phone = useContentToken('footer.phone');
  const cortButtonText = useContentToken('footer.cort_button_text');
  const cortLeadFormId = ((usePropertyField('custom_cort_lead_form_id') as string) || '').trim();

  // Prefetch CORT assets whenever this property has a form ID so the first CTA
  // click doesn't wait on the network. Same contract as `cort-lead-form`.
  useEffect(() => {
    if (!cortLeadFormId) return;
    let cancelled = false;
    void ensureCortFormReady(cortLeadFormId).then(() => {
      if (cancelled) return;
    });
    return () => {
      cancelled = true;
    };
  }, [cortLeadFormId]);

  const openCortModal = React.useCallback(
    (event?: React.MouseEvent) => {
      // Capture-phase handler (below) opens the modal before EditableToken's
      // edit-mode click capture can swallow the event. Stop here so the token
      // editor doesn't also steal the click when authors are verifying the CTA.
      event?.preventDefault();
      event?.stopPropagation();
      (event?.nativeEvent as Event & { stopImmediatePropagation?: () => void } | undefined)
        ?.stopImmediatePropagation?.();

      if (!cortLeadFormId) return;

      void (async () => {
        const modal = await ensureCortFormReady(cortLeadFormId);
        modal?.open?.();
      })();
    },
    [cortLeadFormId],
  );

  // ---- Contrast-driven logo variant selection ----
  // Decide whether text-on-primary should be white or dark using the same
  // `getContrastTextColor` logic the tokens generator uses to compute
  // `--vibe-color-text-on-primary`. We then pick mono_white / mono_black logo
  // defaults (and the Multihub + Repli marks) to match that decision, so the
  // footer logos always sit on the correct contrast side regardless of how
  // dark/light the property's primary brand color is. The runtime-adjusted
  // slot definitions are passed to both `useSlotGroup` and `EditableSlotGroup`
  // so the editor surfaces the same default the renderer is using.
  const primaryColor = usePrimaryColor();
  const textOnPrimary = getContrastTextColor(primaryColor);
  const useWhiteOnPrimary = textOnPrimary === TEXT_ON_PRIMARY_LIGHT;

  // ---- Secondary-bg contrast for the lower footer row ----
  // The bottom credit bar (Managed By / Copyright / Multihub / Repli) uses the
  // theme's secondary color as its background, distinct from the primary
  // background used by the rest of the footer.
  //
  // CRITICAL: This pivot has to match whatever the runtime emits for the
  // `--vibe-color-text-on-secondary` CSS variable, because the band mixes two
  // kinds of consumers:
  //   1. CSS-var consumers — compliance icons, legal-nav text, the
  //      "Professionally Managed by" / "Powered by" / "Design by" /
  //      copyright labels — all read `var(--vibe-color-text-on-secondary)`
  //      via the `text-secondary-foreground` Tailwind class or an inline
  //      arbitrary `text-[var(--css-variable)]` utility.
  //   2. Variant-swap consumers — the Multihub mark, Repli mark, and the
  //      Managed By logo's `defaultField` pick (`property_company_mono_white_logo`
  //      vs `property_company_mono_black_logo`) — pick a hard-coded asset
  //      based on a binary white/black decision.
  //
  // If we recompute the binary decision locally from `useSecondaryColor()`
  // alone, it can disagree with the CSS variable: the runtime token chain
  // (`defaults` → theme `baseTokens` → property-computed) may emit a
  // `textOnSecondary` that wasn't derived from the current `secondary` hex
  // — e.g. `theme-rise/tokens.ts` hardcodes `textOnSecondary: '#FFFFFF'`,
  // which would stay white even on a light secondary swatch that didn't
  // come through `property_secondary_color`. The result was the
  // "icons/text black, Multihub/Repli white" mix-and-match the lower band
  // was previously rendering.
  //
  // Anchor the binary decision to the same merged token the CSS variable
  // resolves to. Whatever value `tokens.color.textOnSecondary` ends up
  // being — whether it was auto-computed, theme-hardcoded, or
  // property-overridden — the SVG variant picks now move in lockstep
  // with the icon/text colors.
  const tokens = useTokens();
  const useWhiteOnSecondary = isWhiteish(tokens.color.textOnSecondary);

  const runtimeLogoSlotDef: SlotGroupDefinition = useWhiteOnPrimary
    ? logoSlotDef
    : {
        ...logoSlotDef,
        fields: {
          ...logoSlotDef.fields,
          image: {
            ...logoSlotDef.fields.image,
            defaultField: 'property_mono_black_logo',
          },
        },
      };

  // Managed By logo is rendered in the LOWER footer band, which uses
  // `--vibe-color-secondary` as its background — not the primary swatch that
  // wraps the main footer body. So unlike `runtimeLogoSlotDef` above (which
  // sits on the primary bg and correctly pivots on `useWhiteOnPrimary`), this
  // one has to pivot on `useWhiteOnSecondary` or the white/black mono default
  // can end up picked against the wrong contrast side — e.g. a property with
  // a dark primary + light secondary would otherwise default to the
  // `mono_white` company logo and render it invisible on the light secondary
  // band. Same pivot drives the Multihub / Repli swap a few rows below.
  const runtimeManagedByLogoDef: SlotGroupDefinition = useWhiteOnSecondary
    ? managedByLogoDef
    : {
        ...managedByLogoDef,
        fields: {
          ...managedByLogoDef.fields,
          image: {
            ...managedByLogoDef.fields.image,
            defaultField: 'property_company_mono_black_logo',
          },
        },
      };

  const logo = useSlotGroup('footer.logo', runtimeLogoSlotDef);
  const managedByLogo = useSlotGroup('footer.managed_by_logo', runtimeManagedByLogoDef);
  const poweredByLogo = useSlotGroup('footer.powered_by_logo', poweredByLogoDef);
  const designByLogo = useSlotGroup('footer.design_by_logo', designByLogoDef);
  const addressLink = useSlotGroup('footer.address', addressLinkDef);
  // Weidner-only: Text Us! link
  const textUsLink = useSlotGroup('footer.text_us_link', textUsLinkDef);
  const accessibilityLink = useSlotGroup('footer.accessibility_link', accessibilityLinkDef);
  const privacyPolicyLink = useSlotGroup('footer.privacy_policy_link', privacyPolicyLinkDef);
  const sitemapLink = useSlotGroup('footer.sitemap_link', sitemapLinkDef);
  // Weidner-only program logos rendered as a horizontal pair beneath the
  // community logo. Each slot exposes both a light (`image`, used on dark
  // primary backgrounds) and dark (`image_dark`, used on light primary
  // backgrounds) variant; the renderer picks one based on the same
  // `useWhiteOnPrimary` luminance pivot the parent uses for the community,
  // managed-by, Multihub, and Repli logos.
  //
  // If only one variant is uploaded (the common case — most properties only
  // upload a single mono logo), we still render it but apply a CSS
  // `filter: brightness(0)` (white → black) or `filter: brightness(0)
  // invert(1)` (black → white) so the single asset flips brightness to
  // remain readable on the opposite-luminance background. This mirrors the
  // CONTRAST behavior of the rest of the footer (text + asset-swap logos)
  // without requiring a second editor upload.
  const weidnerCaresLogo = useSlotGroup('footer.weidner_cares_logo', weidnerCaresLogoDef);
  const loveNotesLogo = useSlotGroup('footer.love_notes_logo', loveNotesLogoDef);

  // Social link slot groups — rendered as an icon row beneath the office
  // hours block in Column 3. Each slot defaults to the matching
  // `property_<platform>_url` field; any whose URL is empty is suppressed
  // inline so the row stays compact for properties with only a couple of
  // platforms configured. We render each slot as its own
  // `<EditableSlotGroup>` with a literal `groupPath` (rather than mapping
  // over an array of dynamic paths) so the schema linter can statically
  // match each wrapper to its `slotGroups` entry.
  const facebookSocial = useSlotGroup('footer.facebook_social', facebookSocialDef);
  const instagramSocial = useSlotGroup('footer.instagram_social', instagramSocialDef);
  const twitterSocial = useSlotGroup('footer.twitter_social', twitterSocialDef);
  const youtubeSocial = useSlotGroup('footer.youtube_social', youtubeSocialDef);
  const tiktokSocial = useSlotGroup('footer.tiktok_social', tiktokSocialDef);
  const linkedinSocial = useSlotGroup('footer.linkedin_social', linkedinSocialDef);

  // True when at least one social URL is set — used to skip the entire icon
  // row (and its wrapper margin) for properties with no socials configured.
  const hasAnySocial =
    hasValue(facebookSocial.url) ||
    hasValue(instagramSocial.url) ||
    hasValue(twitterSocial.url) ||
    hasValue(youtubeSocial.url) ||
    hasValue(tiktokSocial.url) ||
    hasValue(linkedinSocial.url);

  // Resolve the rendered URL + whether to apply a brightness-flip filter.
  // - On a dark primary (`useWhiteOnPrimary` = true) we want a white logo.
  //   If `image` (the white variant) is uploaded → use it, no filter.
  //   Else fall back to `image_dark` (the black variant) and flip it white
  //   with `brightness(0) invert(1)`.
  // - On a light primary (`useWhiteOnPrimary` = false) we want a black logo.
  //   If `image_dark` is uploaded → use it, no filter.
  //   Else fall back to `image` (the white variant) and flip it black with
  //   `brightness(0)`.
  function resolveProgramLogo(slot: { image?: string; image_dark?: string }) {
    if (useWhiteOnPrimary) {
      if (slot.image) return { url: slot.image, filter: undefined as string | undefined };
      if (slot.image_dark) return { url: slot.image_dark, filter: 'brightness(0) invert(1)' };
      return { url: '', filter: undefined };
    }
    if (slot.image_dark) return { url: slot.image_dark, filter: undefined as string | undefined };
    if (slot.image) return { url: slot.image, filter: 'brightness(0)' };
    return { url: '', filter: undefined };
  }
  const weidnerCaresRender = resolveProgramLogo(weidnerCaresLogo);
  const loveNotesRender = resolveProgramLogo(loveNotesLogo);

  // Weidner-only: suppress the Text Us! link when no `custom_text_us_url` is
  // set on the property (and no editor override has been provided) — Weidner
  // properties without an SMS landing page should not render a dead link.
  const showTextUsLink = hasValue(textUsLink.url);

  const officeHours = useCollectionBinding<OfficeHour>('footer.office_hours', {
    collection: 'office_hours',
  });

  const groupedHours = officeHours && officeHours.length > 0
    ? groupOfficeHours(officeHours)
    : [];

  // ─── RentCafe IQ chatbot z-index pin ────────────────────────────────────────
  // Synced from `@vibesites/theme-rise/components/Footer.tsx`. Because this
  // file overrides the parent Footer in the registry, the parent's chatbot
  // z-index code never mounts on Weidner properties unless it's mirrored
  // here. See the maintenance checklist at the top of this file.
  //
  // The Yardi RentCafe IQ widget is injected at runtime by an external script
  // and sets its own inline `style="z-index: …"` on the launcher after mount,
  // which beats anything a stylesheet declares. The CSS rule further down in
  // the render covers the known launcher selectors as a cheap first pass; this
  // effect is the authoritative fix.
  //
  // Approach: watch document.body for added/changed nodes that match the real
  // launcher only (`.rcc-app-cta-wrap`, `.rcc-app-controls`,
  // `#conversations-webchat-toggle`). Pin those to 120 with `!important` —
  // just above the open header chrome — so the vendor inline style still
  // loses. Only also pin an outermost `position: fixed` ancestor when that
  // ancestor is the chat wrap itself; never lift unrelated fixed shells.
  //
  // Scoped to the Footer because Footer is rendered on every page via
  // DefaultLayout, so we get a single global mount point without having to
  // introduce a new always-on component.
  useEffect(() => {
    if (typeof document === 'undefined') return;

    const LAUNCHER_SELECTOR =
      '.rcc-app-cta-wrap, .rcc-app-controls, #conversations-webchat-toggle';
    const CHAT_WRAP_SELECTOR = '.rcc-app-cta-wrap';
    const Z = '120';

    function isLauncher(el: Element): boolean {
      return el.matches(LAUNCHER_SELECTOR);
    }

    function outermostFixedAncestor(el: Element): HTMLElement | null {
      let cur: Element | null = el;
      let last: HTMLElement | null = null;
      while (cur && cur !== document.body && cur instanceof HTMLElement) {
        const pos = getComputedStyle(cur).position;
        if (pos === 'fixed') last = cur;
        cur = cur.parentElement;
      }
      return last;
    }

    function pin(el: HTMLElement) {
      el.style.setProperty('z-index', Z, 'important');
    }

    function processCandidate(el: Element) {
      if (!(el instanceof HTMLElement)) return;
      if (!isLauncher(el)) return;
      pin(el);
      // Only pin a fixed ancestor when it is the chat wrap — never an
      // unrelated outermost fixed shell the launcher happens to sit in.
      const fixedAncestor = outermostFixedAncestor(el);
      if (
        fixedAncestor &&
        fixedAncestor !== el &&
        fixedAncestor.matches(CHAT_WRAP_SELECTOR)
      ) {
        pin(fixedAncestor);
      }
    }

    function scanSubtree(root: ParentNode) {
      const candidates = root.querySelectorAll(LAUNCHER_SELECTOR);
      for (const c of Array.from(candidates)) processCandidate(c);
      // The root node itself can also be a match (querySelectorAll skips it).
      if (root instanceof Element && isLauncher(root)) processCandidate(root);
    }

    // Initial pass — catches the widget if it's already mounted by the time
    // the Footer hydrates (e.g. injected from <head> before React boots).
    scanSubtree(document.body);

    // Coalesce bursts of mutations into a single rAF-scheduled scan so we
    // don't thrash on chat widgets that animate (every transform tick fires
    // an attribute mutation).
    let scheduled = false;
    const observer = new MutationObserver((records) => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        for (const r of records) {
          for (const node of Array.from(r.addedNodes)) {
            if (node instanceof Element) scanSubtree(node);
          }
          if (r.type === 'attributes' && r.target instanceof Element) {
            processCandidate(r.target);
          }
        }
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      // Only react to attribute changes that could affect identification or
      // stacking — anything else (e.g. inline transforms during animation) is
      // noise we want to ignore.
      attributeFilter: ['id', 'class', 'style'],
    });

    return () => observer.disconnect();
  }, []);

  return (
    <footer
      className="text-primary-foreground"
      style={{ backgroundColor: 'var(--vibe-color-primary)' }}
    >

      {/* ---- Main Footer Content ---- */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-6 md:pt-10 md:pb-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-8">

          {/* Column 1: Logo + Weidner program icons */}
          <div className="flex flex-col items-center gap-5">
            <EditableSlotGroup groupPath="footer.logo" definition={runtimeLogoSlotDef}>
              <a href="/" className="block">
                {logo.image ? (
                  <VibesitesImage
                    url={logo.image}
                    alt={logo.alt || 'Community Logo'}
                    className="h-20 w-auto max-w-[190px] object-contain"
                  />
                ) : (
                  <div className="font-heading text-2xl text-primary-foreground">Our Community</div>
                )}
              </a>
            </EditableSlotGroup>

            {/* Weidner Cares + Love Notes program icons — stacked horizontally
                directly beneath the community logo. Each anchor only renders
                when an image has been uploaded to its slot group (matching the
                `managedByLogo` convention below) so empty slots don't leave
                dead links in the rendered footer.

                Contrast pivot: when the property's primary color is dark, the
                footer renders white text + white logos and the program logos
                use the `image` field (treated as the light/white-on-dark
                variant). When the primary is light enough to flip the rest of
                the footer to dark text (per the `useWhiteOnPrimary` luminance
                check above), the program logos use `image_dark` instead. Falls
                back to whichever variant is uploaded if only one is provided,
                so single-asset properties still render a logo. */}
            <div className="flex items-center justify-center gap-6">
              <EditableSlotGroup groupPath="footer.weidner_cares_logo" definition={weidnerCaresLogoDef}>
                {weidnerCaresRender.url ? (
                  <a
                    href={weidnerCaresLogo.url || 'https://www.weidner.com/weidnercares'}
                    target={weidnerCaresLogo._target || '_blank'}
                    rel="noopener noreferrer"
                    aria-label="Weidner Cares"
                    className="inline-flex transition-[filter] duration-200 hover:brightness-110"
                  >
                    <VibesitesImage
                      url={weidnerCaresRender.url}
                      alt={weidnerCaresLogo.alt || 'Weidner Cares'}
                      className="h-10 w-auto max-w-[120px] object-contain"
                      style={weidnerCaresRender.filter ? { filter: weidnerCaresRender.filter } : undefined}
                    />
                  </a>
                ) : null}
              </EditableSlotGroup>

              <EditableSlotGroup groupPath="footer.love_notes_logo" definition={loveNotesLogoDef}>
                {loveNotesRender.url ? (
                  <a
                    href={loveNotesLogo.url || 'https://www.weidner.com/lovenotes'}
                    target={loveNotesLogo._target || '_blank'}
                    rel="noopener noreferrer"
                    aria-label="Love Notes"
                    className="inline-flex transition-[filter] duration-200 hover:brightness-110"
                  >
                    <VibesitesImage
                      url={loveNotesRender.url}
                      alt={loveNotesLogo.alt || 'Love Notes'}
                      className="h-10 w-auto max-w-[120px] object-contain"
                      style={loveNotesRender.filter ? { filter: loveNotesRender.filter } : undefined}
                    />
                  </a>
                ) : null}
              </EditableSlotGroup>
            </div>
          </div>

          {/* Column 2: Contact Info */}
          <div className="flex flex-col items-center gap-5">
            {/* Phone */}
            <div className="text-center">
              <RichTextContent path="footer.call_us_label" defaultValue="<p>Call Us</p>" as="p" className="font-body text-[11px] uppercase tracking-[0.15em] text-primary-foreground mb-1" />
              <a
                href={`tel:${(phone || '').replace(/[^+\d]/g, '')}`}
                className="dniphone dniphonehref font-body text-[13px] text-primary-foreground hover:text-[var(--vibe-color-text-on-primary)]/70 transition-colors"
                style={{ fontFamily: 'var(--vibe-font-body)' }}
              >
                <EditableToken
                  path="footer.phone"
                  defaultField="property_call_tracking_phone"
                  defaultValue="(555) 555-5555"
                >
                  {formatPhone(phone)}
                </EditableToken>
              </a>
            </div>

            {/* Text Us! — Weidner-only. Only renders when `custom_text_us_url`
                (or an editor URL override) is set; properties without an SMS
                landing page get no row here at all. This block is the sole
                visible divergence from the rise theme's Footer — everything
                else above and below mirrors the parent.

                Styled as a borderless pill CTA (rather than the plain
                underlined link the rest of the footer uses) so the SMS option
                reads as a deliberate call-to-action between the Phone and
                Address blocks. The label, icon, and hover opacity all use
                `text-primary-foreground`, which resolves to the contrast-safe
                `--vibe-color-text-on-primary` value — same contrast pivot the
                logos use above — so the button stays legible on both dark and
                light primary brand colors without needing a visible border. */}
            {showTextUsLink && (
              <div className="text-center">
                <EditableSlotGroup groupPath="footer.text_us_link" definition={textUsLinkDef}>
                  <a
                    href={textUsLink.url || '#'}
                    target={textUsLink._target || '_blank'}
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 font-body text-sm font-semibold uppercase tracking-[0.12em] text-primary-foreground hover:opacity-70 transition-opacity"
                    style={{ fontFamily: 'var(--vibe-font-body)' }}
                  >
                    <svg
                      aria-hidden="true"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                    </svg>
                    {textUsLink.text || 'Text Us!'}
                  </a>
                </EditableSlotGroup>
              </div>
            )}

            {/* Address */}
            <div className="text-center">
              <RichTextContent path="footer.visit_us_label" defaultValue="<p>Visit Us</p>" as="p" className="font-body text-[11px] uppercase tracking-[0.15em] text-primary-foreground mb-1" />
              <EditableSlotGroup groupPath="footer.address" definition={addressLinkDef}>
                <a
                  href={addressLink.url || '#'}
                  target={addressLink._target || '_blank'}
                  rel="noopener noreferrer"
                  className="font-body text-[13px] text-[var(--vibe-color-text-on-primary)]/80 hover:text-primary-foreground transition-colors block"
                >
                  {addressLink.text || '123 Main Street, City, ST 00000'}
                </a>
              </EditableSlotGroup>
            </div>
          </div>

          {/* Column 3: Office Hours + Social Icons */}
          <div className="flex flex-col items-center">
            <RichTextContent path="footer.office_hours_label" defaultValue="<p>Office Hours</p>" as="p" className="font-body text-[11px] uppercase tracking-[0.15em] text-primary-foreground mb-3" />
            {groupedHours.length > 0 ? (
              <dl className="space-y-2 w-full max-w-[280px]">
                {groupedHours.map((group, index) => (
                  <div key={index} className="flex justify-between font-body text-sm text-[var(--vibe-color-text-on-primary)]/80">
                    <dt>
                      {/* Full names on mobile and desktop; abbreviated on tablet
                          (md → lg) where the 3-column grid leaves the hours
                          column too narrow for "Monday - Friday". */}
                      <span className="md:hidden lg:inline">{group.label}</span>
                      <span className="hidden md:inline lg:hidden">{group.shortLabel}</span>
                    </dt>
                    <dd className="text-right ml-4">{group.hours}</dd>
                  </div>
                ))}
              </dl>
            ) : (
              <p className="font-body text-sm text-[var(--vibe-color-text-on-primary)]/40">Office hours will be displayed here.</p>
            )}

            {/* CORT lead form CTA — hidden unless the property has a CORT form ID */}
            {cortLeadFormId ? (
              <div className="mt-5 flex justify-center md:justify-start">
                {/* Capture-phase click so the CTA still opens in the editor —
                    EditableToken's edit-mode onClickCapture would otherwise
                    swallow bubbles from the label before button onClick runs. */}
                <button
                  type="button"
                  onClickCapture={openCortModal}
                  className="footer-cort-btn group/cort relative inline-flex items-center justify-center gap-2 text-[var(--vibe-color-text-on-secondary,white)] font-semibold uppercase transition-all duration-300 hover:-translate-y-0.5"
                  style={{
                    background: 'linear-gradient(135deg, var(--vibe-color-secondary) 0%, color-mix(in srgb, var(--vibe-color-secondary) 80%, black) 100%)',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.2), 0 4px 16px color-mix(in srgb, var(--vibe-color-secondary) 30%, transparent)',
                  }}
                >
                  <EditableToken path="footer.cort_button_text" as="span" className="footer-cort-btn-label relative z-10 pointer-events-none">
                    {cortButtonText || 'Furnish Your Home with CORT'}
                  </EditableToken>
                  <svg className="footer-cort-btn-icon relative z-10 shrink-0 transition-transform duration-300 group-hover/cort:translate-x-0.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
                  </svg>
                </button>
              </div>
            ) : null}

            {/* Social icons — rendered below the hours block. Each platform's
                URL defaults to its `property_<platform>_url` field; icons whose
                URL resolves to empty (or "false"/"null", per `hasValue`) are
                skipped inline so the row stays compact. The icons render with
                `color="currentColor"` so they inherit `text-primary-foreground`
                and stay contrast-correct on both dark and light primary brand
                colors — same pattern as the compliance icons in the lower
                band.

                Each `<EditableSlotGroup>` uses a literal `groupPath` (rather
                than a value pulled from a loop variable) so the schema linter
                can statically match each wrapper to its `slotGroups` entry. */}
            {hasAnySocial && (
              <div className="mt-6 flex items-center justify-center gap-4 text-primary-foreground">
                {hasValue(facebookSocial.url) && (
                  <EditableSlotGroup groupPath="footer.facebook_social" definition={facebookSocialDef}>
                    <a
                      href={facebookSocial.url || '#'}
                      target={facebookSocial._target || '_blank'}
                      rel="noopener noreferrer"
                      aria-label="Facebook"
                      className="inline-flex items-center justify-center opacity-90 hover:opacity-100 transition-opacity"
                    >
                      <FacebookIcon size={20} color="currentColor" aria-label="Facebook" />
                    </a>
                  </EditableSlotGroup>
                )}
                {hasValue(instagramSocial.url) && (
                  <EditableSlotGroup groupPath="footer.instagram_social" definition={instagramSocialDef}>
                    <a
                      href={instagramSocial.url || '#'}
                      target={instagramSocial._target || '_blank'}
                      rel="noopener noreferrer"
                      aria-label="Instagram"
                      className="inline-flex items-center justify-center opacity-90 hover:opacity-100 transition-opacity"
                    >
                      <InstagramIcon size={20} color="currentColor" aria-label="Instagram" />
                    </a>
                  </EditableSlotGroup>
                )}
                {hasValue(twitterSocial.url) && (
                  <EditableSlotGroup groupPath="footer.twitter_social" definition={twitterSocialDef}>
                    <a
                      href={twitterSocial.url || '#'}
                      target={twitterSocial._target || '_blank'}
                      rel="noopener noreferrer"
                      aria-label="Twitter / X"
                      className="inline-flex items-center justify-center opacity-90 hover:opacity-100 transition-opacity"
                    >
                      <TwitterIcon size={20} color="currentColor" aria-label="Twitter / X" />
                    </a>
                  </EditableSlotGroup>
                )}
                {hasValue(youtubeSocial.url) && (
                  <EditableSlotGroup groupPath="footer.youtube_social" definition={youtubeSocialDef}>
                    <a
                      href={youtubeSocial.url || '#'}
                      target={youtubeSocial._target || '_blank'}
                      rel="noopener noreferrer"
                      aria-label="YouTube"
                      className="inline-flex items-center justify-center opacity-90 hover:opacity-100 transition-opacity"
                    >
                      <YouTubeIcon size={20} color="currentColor" aria-label="YouTube" />
                    </a>
                  </EditableSlotGroup>
                )}
                {hasValue(tiktokSocial.url) && (
                  <EditableSlotGroup groupPath="footer.tiktok_social" definition={tiktokSocialDef}>
                    <a
                      href={tiktokSocial.url || '#'}
                      target={tiktokSocial._target || '_blank'}
                      rel="noopener noreferrer"
                      aria-label="TikTok"
                      className="inline-flex items-center justify-center opacity-90 hover:opacity-100 transition-opacity"
                    >
                      <TikTokIcon size={20} color="currentColor" aria-label="TikTok" />
                    </a>
                  </EditableSlotGroup>
                )}
                {hasValue(linkedinSocial.url) && (
                  <EditableSlotGroup groupPath="footer.linkedin_social" definition={linkedinSocialDef}>
                    <a
                      href={linkedinSocial.url || '#'}
                      target={linkedinSocial._target || '_blank'}
                      rel="noopener noreferrer"
                      aria-label="LinkedIn"
                      className="inline-flex items-center justify-center opacity-90 hover:opacity-100 transition-opacity"
                    >
                      <LinkedInIcon size={20} color="currentColor" aria-label="LinkedIn" />
                    </a>
                  </EditableSlotGroup>
                )}
              </div>
            )}
          </div>

        </div>
      </div>

      {/* ---- Lower Footer Band (secondary-color) ----
          Full-width secondary-bg wrapper containing two stacked sections:
            1. Compliance icons + legal/accessibility nav
            2. Responsive credit row (Managed By / Multihub / Repli / Copyright)

          The previous HR divider between the legal nav above and this band
          was removed — the color transition from primary to secondary now
          serves as the visual separator, so a hairline rule on top of it
          would just be noise. All text inside this band uses the
          `--vibe-color-text-on-secondary` contrast token so it stays legible
          on any secondary swatch (white-on-dark or black-on-light, picked
          automatically by `useWhiteOnSecondary` for the inline logos).

          ---- Bottom credit row layout notes ----
          Single flat flex container with CSS `order` rewiring + per-breakpoint
          width tricks to drive two very different layouts from one render path:

          Mobile (default):
            Row 1: Managed By (centered, full width via `w-full`)
            Row 2: Powered by (Multihub) + Website Design by (Repli) — paired,
                   centered by parent `justify-center`
            Row 3: Copyright (centered, full width via `w-full`)
            Labels are visible on every breakpoint now (per user request);
            Managed By and Copyright each get `w-full` so they grab a whole
            flex-wrap row, leaving Multihub+Repli to share row 2 together.

          Desktop (md+):
            Single row: [Managed By] [Copyright] →→ [Multihub] [Repli]
            Managed By keeps `order-1`; Copyright is moved to `md:order-2` and
            given `md:w-auto md:mr-auto` — the auto right-margin consumes all
            leftover space, pushing the Multihub/Repli pair (orders 3 & 4) to
            the right edge.

          Each `<EditableSlotGroup>` is itself the flex item (we apply order +
          mobile centering via its `className` prop) so we don't introduce
          wrapper divs that would interfere with the editor's hover/click
          affordances on the slot. For the Managed By group, `w-full flex
          justify-center` on mobile makes the wrapper a centering flex
          container so the inner `<a>` (which has its own `flex items-center
          gap-2`) is centered horizontally; `md:w-auto md:justify-start` reverts
          to natural content-width left-aligned on desktop. */}
      {/* The entire lower band is forced to `text-secondary-foreground` at the
          outer wrapper so EVERY child element resolves `color: inherit` to
          `var(--vibe-color-text-on-secondary)` rather than falling back up the
          DOM to the `<footer>` element's `text-primary-foreground`.
          ----
          Why this matters: the Vibesites Tailwind preset declares its color
          tokens as raw `var(--vibe-color-*)` references with no
          `<alpha-value>` placeholder, so Tailwind's slash-opacity modifiers
          (`text-secondary-foreground/70`, `text-[var(--vibe-…)]/60`, etc.)
          silently emit invalid CSS — the rule's value becomes
          `rgb(var(--…) / 0.x)` which is invalid because `var()` resolves to
          a hex string, not an `R G B` triplet. The browser drops the
          declaration and the element inherits its parent's color. Before
          this wrapper class was added, the lower band's slash-modifier
          children (compliance icons, separator dots, copyright) inherited
          from the parent `<footer className="text-primary-foreground">` and
          rendered in `--vibe-color-text-on-primary`, while siblings using
          plain `text-secondary-foreground` (legal-nav text, Managed By
          label, Powered By / Design By labels) correctly rendered in
          `--vibe-color-text-on-secondary`. That mismatch — and only when
          primary's and secondary's contrast tokens disagreed (e.g. light
          primary + dark secondary) — produced the "some elements black, some
          white" symptom in this band.
          ----
          Anchoring inheritance to `text-secondary-foreground` here makes the
          broken slash classes inherit *secondary*-foreground instead of
          *primary*-foreground, so even the visually-dropped opacity rules
          end up with the correct base color. The slash modifiers themselves
          still no-op (you don't get the dim effect), but the color side of
          the lower band is now uniform across every element. */}
      <div className="text-secondary-foreground" style={{ backgroundColor: 'var(--vibe-color-secondary)' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-6">

          {/* Compliance Icons + Legal/Accessibility Nav */}
          <div className="flex flex-col items-center gap-4 mb-6">
            {/* Compliance icons in a row. `text-secondary-foreground` here is
                redundant with the lower-band wrapper but kept explicit so a
                future refactor that splits this section out still renders
                the icons in the right contrast color. */}
            <div className="flex items-center justify-center gap-5 text-secondary-foreground">
              <WheelchairIcon size={28} color="currentColor" />
              <FairHousingIcon size={28} color="currentColor" />
              <PetsAllowedIcon size={28} color="currentColor" />
            </div>

            {/* Legal nav links with dot separators. Hover state can't use the
                slash-opacity modifier (it silently no-ops on var()-based
                colors — see the wrapper comment above), so we use a solid
                hover color via `hover:opacity-70` instead. */}
            <nav className="flex flex-wrap items-center justify-center gap-x-1 gap-y-1 font-body text-[13px] text-secondary-foreground" aria-label="Legal links">
              <EditableSlotGroup groupPath="footer.accessibility_link" definition={accessibilityLinkDef}>
                <a href={accessibilityLink.url || '/accessibility'} target={accessibilityLink._target || '_self'} className="hover:opacity-70 transition-opacity">
                  {accessibilityLink.text || 'Accessibility'}
                </a>
              </EditableSlotGroup>
              <span aria-hidden="true" className="text-secondary-foreground">·</span>
              <EditableSlotGroup groupPath="footer.privacy_policy_link" definition={privacyPolicyLinkDef}>
                <a href={privacyPolicyLink.url || '/privacy-policy'} target={privacyPolicyLink._target || '_self'} className="hover:opacity-70 transition-opacity">
                  {privacyPolicyLink.text || 'Privacy Policy'}
                </a>
              </EditableSlotGroup>
              <span aria-hidden="true" className="text-secondary-foreground">·</span>
              <EditableSlotGroup groupPath="footer.sitemap_link" definition={sitemapLinkDef}>
                <a href={sitemapLink.url || '/sitemap'} target={sitemapLink._target || '_self'} className="hover:opacity-70 transition-opacity">
                  {sitemapLink.text || 'Sitemap'}
                </a>
              </EditableSlotGroup>
            </nav>
          </div>

          {/* Bottom Bar: Responsive Stacked / Two-Zone Credit Row.
              `text-secondary-foreground` is again redundant with the wrapper
              but explicit for the same reason as the icons row above. */}
          <div className="flex flex-wrap items-center justify-center md:justify-start gap-x-6 gap-y-3 font-body text-[10px] text-secondary-foreground">

            {/* Managed By — mobile: row 1 alone (w-full + center), desktop: order-1 left zone */}
            <EditableSlotGroup
              groupPath="footer.managed_by_logo"
              definition={runtimeManagedByLogoDef}
              className="order-1 w-full flex justify-center md:w-auto md:justify-start"
            >
              {managedByLogo.image ? (
                <a
                  href={managedByLogo.url || '#'}
                  target={managedByLogo._target || '_blank'}
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 opacity-90 hover:opacity-100 transition-opacity"
                >
                  <RichTextContent
                    path="footer.managed_by_text"
                    defaultValue="<p>Professionally Managed by</p>"
                    as="span"
                    className="text-secondary-foreground"
                  />
                  <VibesitesImage
                    url={managedByLogo.image}
                    alt={managedByLogo.alt || 'Property Management Company'}
                    className="h-4 w-auto max-w-[110px] object-contain"
                  />
                </a>
              ) : null}
            </EditableSlotGroup>

            {/* Powered By (Multihub) — mobile: row 2 left half (paired with Repli),
                desktop: order-3 right zone */}
            <EditableSlotGroup
              groupPath="footer.powered_by_logo"
              definition={poweredByLogoDef}
              className="order-2 md:order-3"
            >
              <a
                href={poweredByLogo.url || 'https://www.multihub.io/'}
                target={poweredByLogo._target || '_blank'}
                rel="noopener noreferrer"
                className="flex items-center gap-2 opacity-90 hover:opacity-100 transition-opacity"
              >
                <RichTextContent
                  path="footer.powered_by_text"
                  defaultValue="<p>Powered by</p>"
                  as="span"
                  className="text-secondary-foreground"
                />
                {useWhiteOnSecondary ? (
                  <MultihubLogoMonoWhite height={14} alt={poweredByLogo.alt || 'Multihub'} />
                ) : (
                  <MultihubLogoMonoBlack height={14} alt={poweredByLogo.alt || 'Multihub'} />
                )}
              </a>
            </EditableSlotGroup>

            {/* Website Design By (Repli) — mobile: row 2 right half (paired with Multihub),
                desktop: order-4 right zone */}
            <EditableSlotGroup
              groupPath="footer.design_by_logo"
              definition={designByLogoDef}
              className="order-3 md:order-4"
            >
              <a
                href={designByLogo.url || 'https://www.repli360.com/'}
                target={designByLogo._target || '_blank'}
                rel="noopener noreferrer"
                className="flex items-center gap-2 opacity-90 hover:opacity-100 transition-opacity"
              >
                <RichTextContent
                  path="footer.design_by_text"
                  defaultValue="<p>Website Design by</p>"
                  as="span"
                  className="text-secondary-foreground"
                />
                <VibesitesImage
                  url={useWhiteOnSecondary ? repliWhiteLogoUrl : repliBlackLogoUrl}
                  alt={designByLogo.alt || 'Repli Logo'}
                  height={16}
                  className="h-4 w-auto"
                  priority
                />
              </a>
            </EditableSlotGroup>

            {/* Copyright (year auto-updates from the runtime clock so the footer
                never goes stale and no token edit is needed for the year rollover).
                - Mobile: order-4 + `w-full` forces a flex-wrap break onto its own
                  final row, centered.
                - Desktop: order-2 puts it between Managed By and Powered By, and
                  `md:mr-auto` consumes the leftover space so the Multihub/Repli
                  pair gets pushed to the right edge. */}
            {/* `text-secondary-foreground/70` would silently no-op here (the
                color token is a raw `var(--…)` reference with no
                `<alpha-value>` placeholder — see the lower-band wrapper
                comment), and the resulting fallback to the parent's
                inherited color used to put the copyright in
                `--vibe-color-text-on-primary` instead of
                `--vibe-color-text-on-secondary`, which is the inconsistency
                that triggered this fix. Use a solid `text-secondary-foreground`
                + element-level `opacity-70` instead so the dim effect still
                lands but the base color stays correct. */}
            <span className="order-4 w-full text-center md:order-2 md:w-auto md:text-left md:mr-auto text-secondary-foreground opacity-70">
              © {new Date().getFullYear()} All Rights Reserved.
            </span>

          </div>
        </div>
      </div>

      {/* ---- Global z-index fallback for the RentCafe IQ chatbot ----
          Synced from `@vibesites/theme-rise/components/Footer.tsx`. Cheap
          first pass for the known launcher selectors. The authoritative
          fix lives in the `useEffect` above — MutationObserver + inline
          `!important` so the vendor's post-mount inline z-index still loses.
          Kept at 120, just above the open header chrome. */}
      <style>{`
        /* CORT / tingle modal — default z-index is 1000, which sits under
           sticky headers and other theme chrome. Keep it well above the
           RentCafe IQ launcher pin so the lead form stays usable. */
        .tingle-modal {
          z-index: 2147483646 !important;
        }

        .footer-cort-btn {
          width: 100%;
          max-width: 20rem;
          padding: 0.625rem 1.25rem;
          font-size: 0.75rem;
          letter-spacing: 0.1em;
          line-height: 1.25;
        }
        .footer-cort-btn-label {
          text-align: center;
          text-wrap: balance;
          line-height: 1.3;
        }
        .footer-cort-btn-icon {
          width: 0.875rem;
          height: 0.875rem;
        }

        @media (min-width: 768px) and (max-width: 1023px) {
          .footer-cort-btn {
            max-width: none;
            width: 100%;
            padding: 0.625rem 0.875rem;
            font-size: 0.6875rem;
            letter-spacing: 0.06em;
            gap: 0.375rem;
          }
          .footer-cort-btn-icon {
            width: 0.75rem;
            height: 0.75rem;
          }
        }

        @media (min-width: 1024px) {
          .footer-cort-btn {
            width: auto;
            max-width: 100%;
            padding: 0.75rem 1.5rem;
            font-size: 0.8125rem;
            letter-spacing: 0.12em;
            white-space: nowrap;
          }
          .footer-cort-btn-label {
            text-align: left;
            text-wrap: unset;
          }
          .footer-cort-btn-icon {
            width: 1rem;
            height: 1rem;
          }
        }

        /* Production RentCafe Connect / IQ launcher only:
           floating wrap .rcc-app-cta-wrap, controls .rcc-app-controls,
           toggle #conversations-webchat-toggle. */
        .rcc-app-cta-wrap,
        .rcc-app-controls,
        #conversations-webchat-toggle {
          z-index: 120 !important;
        }
      `}</style>

    </footer>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Footer',
  displayName: 'Footer',
  tokens: {
    'footer.phone': {
      label: 'Phone',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      defaultValue: '(555) 555-5555',
    },
    'footer.call_us_label': {
      label: 'Call Us Label',
      type: 'richtext',
      defaultValue: '<p>Call Us</p>',
    },
    'footer.visit_us_label': {
      label: 'Visit Us Label',
      type: 'richtext',
      defaultValue: '<p>Visit Us</p>',
    },
    'footer.office_hours_label': {
      label: 'Office Hours Label',
      type: 'richtext',
      defaultValue: '<p>Office Hours</p>',
    },
    'footer.cort_button_text': {
      label: 'CORT Button Text',
      type: 'text',
      defaultValue: 'Furnish Your Home with CORT',
    },
    'footer.managed_by_text': {
      label: 'Managed By Text',
      type: 'richtext',
      defaultValue: '<p>Professionally Managed by</p>',
    },
    'footer.powered_by_text': {
      label: 'Powered By Text',
      type: 'richtext',
      defaultValue: '<p>Powered by</p>',
    },
    'footer.design_by_text': {
      label: 'Website Design By Text',
      type: 'richtext',
      defaultValue: '<p>Website Design by</p>',
    },
  },
  slotGroups: {
    'footer.logo': logoSlotDef,
    'footer.managed_by_logo': managedByLogoDef,
    'footer.powered_by_logo': poweredByLogoDef,
    'footer.design_by_logo': designByLogoDef,
    'footer.address': addressLinkDef,
    // Weidner-only slot — see `textUsLinkDef` above.
    'footer.text_us_link': textUsLinkDef,
    'footer.accessibility_link': accessibilityLinkDef,
    'footer.privacy_policy_link': privacyPolicyLinkDef,
    'footer.sitemap_link': sitemapLinkDef,
    // Weidner-only program logos.
    'footer.weidner_cares_logo': weidnerCaresLogoDef,
    'footer.love_notes_logo': loveNotesLogoDef,
    // Social link icons rendered below the office hours block in Column 3.
    'footer.facebook_social': facebookSocialDef,
    'footer.instagram_social': instagramSocialDef,
    'footer.twitter_social': twitterSocialDef,
    'footer.youtube_social': youtubeSocialDef,
    'footer.tiktok_social': tiktokSocialDef,
    'footer.linkedin_social': linkedinSocialDef,
  },
  collectionBindings: {
    'footer.office_hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
  },
};

(Footer as any).contentSchema = contentSchema;

export default Footer;
